import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/common/Layout/Layout';
import { useAuth } from '@/hooks/useAuth';
import { statsApi } from '@/services/statistics';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DateRangePicker } from '@/components/ui/date-picker';
import { Separator } from '@/components/ui/separator';
import { RefreshCw, Calendar as CalendarIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BusinessDashboard, PeriodType, StatisticsOutletContext } from './statisticsShared';
import { PERIOD_LABELS } from './statisticsShared';

// ─── Tab-to-route mapping ────────────────────────────────────────────────────

const TAB_ROUTES: Record<string, string> = {
  dashboard: '/statistics',
  team: '/statistics/team',
  clients: '/statistics/clients',
  matters: '/statistics/matters',
  'my-stats': '/statistics/my-stats',
};

const ROUTE_TO_TAB: Record<string, string> = {
  '/statistics': 'dashboard',
  '/statistics/team': 'team',
  '/statistics/clients': 'clients',
  '/statistics/matters': 'matters',
  '/statistics/my-stats': 'my-stats',
};

// ─── Layout Component ────────────────────────────────────────────────────────

export default function StatisticsLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [period, setPeriod] = useState<PeriodType>('year');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BusinessDashboard | null>(null);
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  const preferTableView = !!(user?.preferences as Record<string, any> | undefined)?.prefer_table_view;

  /** Convert "last_month" / "last_quarter" / "last_year" into custom date ranges for the API */
  const buildApiParams = () => {
    const firmId = user?.firms?.[0];
    const base = firmId ? { firm_id: firmId } : {};
    const now = new Date();

    if (period === 'last_month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { ...base, period: 'custom' as const, start_date: fmtDate(start), end_date: fmtDate(end) };
    }
    if (period === 'last_quarter') {
      const currentQ = Math.floor(now.getMonth() / 3);
      const start = new Date(now.getFullYear(), (currentQ - 1) * 3, 1);
      const end = new Date(now.getFullYear(), currentQ * 3, 0);
      return { ...base, period: 'custom' as const, start_date: fmtDate(start), end_date: fmtDate(end) };
    }
    if (period === 'last_year') {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      return { ...base, period: 'custom' as const, start_date: fmtDate(start), end_date: fmtDate(end) };
    }
    if (period === 'custom' && customRange.start && customRange.end) {
      return { ...base, period: 'custom' as const, start_date: customRange.start, end_date: customRange.end };
    }
    return { ...base, period: period as 'month' | 'quarter' | 'year' };
  };

  const fmtDate = (d: Date) => d.toISOString().split('T')[0];

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = buildApiParams();
      const response = await statsApi.getBusinessDashboard(params);
      setData(response);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // For custom period, only fetch when both dates are set
    if (period === 'custom' && (!customRange.start || !customRange.end)) return;
    fetchData();
  }, [period, customRange.start, customRange.end]);

  const hasFinancialData = data?.kpis?.total_revenue !== undefined;
  const labels = PERIOD_LABELS[period];

  // Determine the active tab from the current route
  const activeTab = ROUTE_TO_TAB[location.pathname] ?? 'dashboard';

  const handleTabChange = (tab: string) => {
    const route = TAB_ROUTES[tab];
    if (route) {
      navigate(route);
    }
  };

  // Context to pass to child routes via Outlet
  const outletContext: StatisticsOutletContext = {
    period,
    loading,
    error,
    data,
    hasFinancialData,
    labels,
    fetchData,
    preferTableView,
    ...(period === 'custom' && customRange.start && customRange.end
      ? { customRange: { start: customRange.start, end: customRange.end } }
      : {}),
  };

  // ── Error state ──
  if (error && !data) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Unable to Load Dashboard</h2>
          <p className="text-muted-foreground text-center max-w-md mb-4">{error}</p>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Statistics</h1>
            <p className="text-muted-foreground text-sm">
              Business performance overview
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={(v: PeriodType) => setPeriod(v)}>
              <SelectTrigger className="w-44">
                <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
                <Separator className="my-1" />
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="last_quarter">Last Quarter</SelectItem>
                <SelectItem value="last_year">Last Year</SelectItem>
                <Separator className="my-1" />
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            {period === 'custom' && (
              <DateRangePicker
                value={customRange}
                onChange={setCustomRange}
                placeholder="Pick dates"
                align="end"
                className="h-9"
              />
            )}
            <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* ── Tab Navigation ── */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-5 max-w-2xl">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="team">Team</TabsTrigger>
            <TabsTrigger value="matters">Matters</TabsTrigger>
            <TabsTrigger value="clients">Clients</TabsTrigger>
            <TabsTrigger value="my-stats">My Stats</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* ── Routed Tab Content ── */}
        <div className="mt-6">
          <Outlet context={outletContext} />
        </div>
      </div>
    </Layout>
  );
}
